import { ReactComponent as EditIcon } from "assets/images/icons/icn_edit.svg";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Box, Tabs, Tab, Typography, Divider } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import useZustandStore from "zustandStore";

// Global Utils
import getSwitchPortStatus, { getSwitchPortStatusLabel } from "utils/getSwitchPortStatus";

// Helper
import {
  getSendRequestParams,
  getCleanUpdatePayload
} from './helper';

// Local const
import {
  switchesOptionsData,
  apOptionsData,
  defaultPortTypes,
  linkSpeedOptions,
  rawSpeedToOptionValue,
  defaultProfileList,
  portTypeOptions,
  defaultAlertInfo,
  defaultSelectedModal,
  operationDefinition,
} from "../const";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import useAuthScope from "hooks/useAuthScope";

// Components
import { StyledSelect } from "components/Select/searchWithSelect";
import { SearchWithSelectCheckbox } from "components/Select/searchWithSelectCheckbox";
import SearchInput from "components/SearchInput";
import { SolidRectangleLightButton } from "components/buttons/SolidRectangleLightButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton as SolidRectangleButton } from "components/buttons/SolidRectangleButton";
import ColumnVisibilityToggle from "components/Table/ColumnVisibilityToggle";
import ColumnFilterToggle from "components/Table/ColumnFilterToggle";
import GlobalAlert from "components/Notification";
import TableField from "components/Table";
import DrawerField from "components/Drawer";
import UnSavedConfirmModalContainer from 'components/UnSavedConfirmModalContainer';

// Columns
import getAccessPointsColumns from "./AccessPoints/accessPointsColumns";
import getSwitchesColumns from "./Switches/switchesColumns";
import SwitchesDrawer from "./Switches/Drawer";
import AccessPointsDrawer from "./AccessPoints/Drawer";
import SwitchEditModal from "./Switches/Modal/SwitchEditModal";
import AccessPointEditModal from "./AccessPoints/Modal/AccessPointEditModal";

const WiredPortManagement = ({
  showEditGeneral,
  savedLocationState,
  clearSavedLocationState,
  unSavedBlocker,
  onGoToPage
}) => {
  const [uuids] = useState(() => ({
    read: uuidv4(),
    update: uuidv4(),
    readProfile: uuidv4(),
    readSchedule: uuidv4(),
    readRadiusProfile: uuidv4(),
    readMacProfile: uuidv4(),
    readPortTags: uuidv4(),
  }));
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { auth, isAuthReady } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { isDataChanged, onInitBlockerData, onUpdateBlockerData, isUnsavedBlocked, onBlockerApply, onBlockerDiscard, tabBlocker } = unSavedBlocker;
  const setGlobalOnApplyFn = useZustandStore(state => state.setGlobalOnApplyFn);
  const setGlobalOnDiscardFn = useZustandStore(state => state.setGlobalOnDiscardFn);

  const {
    navigateModelName,
    navigateMacAddress,
    navigatePortProfileName,
    navigatePortDeviceType,
    navigateSelectedPort
  } = savedLocationState || {};

  const [tabValue, setTabValue] = useState(navigatePortDeviceType || "switches");
  const [optionsHover, setOptionsHover] = useState(false);
  const [allSwitchesData, setAllSwitchesData] = useState([]);
  const [switchesData, setSwitchesData] = useState([]);
  const [originalSwitchesData, setOriginalSwitchesData] = useState([]);
  const [allAccessPointsData, setAllAccessPointsData] = useState([]);
  const [accessPointsData, setAccessPointsData] = useState([]);
  const [originalAccessPointsData, setOriginalAccessPointsData] = useState([]);
  const [originalRowData, setOriginalRowData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSwitchEditModal, setOpenSwitchEditModal] = useState(false);
  const [openAPEditModal, setOpenAPEditModal] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [profileAPList, setProfileAPList] = useState([]);
  const [profileSwitchList, setProfileSwitchList] = useState([]);
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [radiusProfiles, setRadiusProfiles] = useState([]);
  const [macProfiles, setMacProfiles] = useState([]);
  const [portTagsMap, setPortTagsMap] = useState({});
  const [alertInfo, setAlertInfo] = useState(cloneDeep(defaultAlertInfo));

  const modelSelectWrapperRef = useRef(null);
  const deviceSelectWrapperRef = useRef(null);
  const isAutoClickRef = useRef(false);

  const [selectedModel, setSelectedModel] = useState(cloneDeep(defaultSelectedModal));

  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;
  let entId = null;
  let orgId = null;

  if (hasManageOrg) {
    entId = realmId;
    if (orgsId) orgId = orgsId;
  } else {
    orgId = realmId;
  }

  const filteredAccessPointsData =
    accessPointsData?.tunnelAccess === true ? [] : accessPointsData;

  const filteredSwitchesData =
    switchesData?.tunnelAccess === true ? [] : switchesData;

  const parseSupportedSpeed = (linkSpeed, portIndex) => {
    if (!Array.isArray(linkSpeed)) return { s: [], t: [] };

    let currentStart = 1;

    for (const item of linkSpeed) {
      const { count, s, t } = item;

      const rangeStart = currentStart;
      const rangeEnd = currentStart + count - 1;

      if (portIndex >= rangeStart && portIndex <= rangeEnd) {
        return {
          s: Array.isArray(s) ? s : [],
          t: Array.isArray(t) ? t : [],
        };
      }

      currentStart += count;
    }

    return { s: [], t: [] };
  };

  const getSupportedSpeedOptions = (linkSpeed, portIndex) => {
    const { s = [], t = [] } = parseSupportedSpeed(linkSpeed, portIndex);

    const convert = (arr) =>
      arr
        .map((raw) => {
          const mapped = rawSpeedToOptionValue[raw];
          return linkSpeedOptions.find((o) => o.value === mapped);
        })
        .filter(Boolean);

    return {
      s: convert(s),
      t: convert(t),
    };
  };

  const apTableInstance = useTableInstance({
    tableName: "accessPoints",
    data: filteredAccessPointsData,
    columns: getAccessPointsColumns(
      selectedModel.accessPoints && selectedModel.accessPoints !== "All Models",
      portTagsMap
    ),
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["deviceName", "lldp"],
    enableRowSelection: true,
    enableColumnPinning: true,
    hiddenColumns: [
      "modelName",
      "lldp",
      "deviceIp",
      "txTraffic",
      "rxTraffic",
      "txMulticast",
      "rxMulticast",
      "txBroadcast",
      "rxBroadcast",
      "txPackets",
      "rxPackets",
      "totalTraffic",
    ],
  });

  const switchTableInstance = useTableInstance({
    tableName: "switches",
    data: filteredSwitchesData,
    columns: getSwitchesColumns(
      selectedModel.switches && selectedModel.switches !== "All Models",
      scheduleOptions,
      portTagsMap,
    ),
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["deviceName", "lldp"],
    enableRowSelection: true,
    enableColumnPinning: true,
    hiddenColumns: [
      "portName",
      "portSchedule",
      "poeSchedule",
      "accessPolicyLabel",
      "mirror",
      "portIsolate",
      "flowControl",
      "modelName",
      "lldp",
      "numberOfIGMPGroup",
      "deviceIp",
      "poePowerUsage",
      "txTraffic",
      "rxTraffic",
      "txMulticast",
      "rxMulticast",
      "txBroadcast",
      "rxBroadcast",
      "txPackets",
      "rxPackets",
      "totalTraffic",
    ],
  });

  const currentTableInstance = useMemo(() => {
    return (
      (tabValue === "switches" && switchTableInstance) ||
      (tabValue === "accessPoints" && apTableInstance)
    );
  }, [tabValue, switchTableInstance, apTableInstance]);

  const selectedRows = useMemo(() => currentTableInstance?.table?.getSelectedRowModel()?.rows, [currentTableInstance?.table?.getSelectedRowModel()?.rows]);

  const formatTabLabel = (tab) => {
    if (tab === "accessPoints") return "Access Points";
    if (tab === "switches") return "Switches";
    return tab;
  };

  // get profile list
  const getProfileList = () => {
    const { apiUrl, requestParams } = getSendRequestParams({ auth, inFix: "profilePorts", method: "read" });

    sendRequest(
      apiUrl,
      uuids.readProfile,
      requestParams
    );
  };

  const getScheduleProfiles = () => {
    const { apiUrl, requestParams } = getSendRequestParams({ auth, inFix: "profileSchedule", method: "read" });
    sendRequest(apiUrl, uuids.readSchedule, requestParams);
  }

  const getMacProfiles = () => {
    const { apiUrl, requestParams } = getSendRequestParams({ auth, inFix: "profileMacAddrs", method: "read" });
    sendRequest(apiUrl, uuids.readMacProfile, requestParams);
  }

  const getRadiusProfiles = () => {
    const { apiUrl, requestParams } = getSendRequestParams({ auth, inFix: "radiusProfile", method: "read" });
    sendRequest(apiUrl, uuids.readRadiusProfile, requestParams);
  }

  const getSpeedOptionsLabel = (value) => {
    const option = linkSpeedOptions.find((opt) => opt.value === value);
    return option ? option.label : "-";
  };

  const getAllWiredData = () => {
    const { apiUrl, requestParams } = getSendRequestParams({ auth, inFix: "ports", method: "read" });
    const payload = {
      data: {
        deviceIds: [],
      },
    }

    sendRequest(
      apiUrl,
      uuids.read,
      requestParams,
      payload,
    );
  };

  const getPortTagsData = () => {
    const { apiUrl, requestParams } = getSendRequestParams({ auth, inFix: "tags/portTags", method: "read" });
    sendRequest(apiUrl, uuids.readPortTags, requestParams);
  };

  const handleTabChange = tabBlocker((event, newValue) => {
    setTabValue(newValue);
    handleDrawerClose();
    setActiveRow(null);
    setOriginalRowData(null);
  });

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleOpenSwitchEditModal = () => {
    setOpenSwitchEditModal(true);
  };

  const handleOpenAPEditModal = () => {
    setOpenAPEditModal(true);
  };

  const handleCloseSwitchEditModal = () => {
    setOpenSwitchEditModal(false);
  };

  const handleCloseAPEditModal = () => {
    setOpenAPEditModal(false);
  };

  const displayGivenModel = () => {
    if (navigateModelName && navigateMacAddress) {
      setSelectedModel((prev) => {
        const updated = {
          ...prev,
          ...(tabValue === "switches"
            ? {
              switches: navigateModelName,
              switchesSub: [navigateMacAddress],
            }
            : {
              accessPoints: navigateModelName,
              accessPointsSub: [navigateMacAddress],
            }),
        };

        if (tabValue === "switches") {
          let filtered = allSwitchesData.filter(
            (item) =>
              item.modelName === navigateModelName &&
              item.macAddress === navigateMacAddress
          );
          setSwitchesData(filtered);
        } else {
          let filtered = allAccessPointsData.filter(
            (item) =>
              item.modelName === navigateModelName &&
              item.macAddress === navigateMacAddress
          );
          setAccessPointsData(filtered);
        }

        return updated;
      });

      const {
        navigateModelName,
        navigateMacAddress,
        ...resetStates
      } = location?.state;

      navigate(location.pathname, {
        replace: true,
        state: resetStates
      });

    } else {
      if (tabValue === "switches") {
        setSwitchesData(allSwitchesData);
      } else {
        setAccessPointsData(allAccessPointsData);
      }
    }

    clearSavedLocationState();
  }

  const onApply = () => {
    const { apiUrl, requestParams } = getSendRequestParams({ auth, inFix: "ports", method: "update" });
    const ports = tabValue === "switches" ? switchesData : (tabValue === "accessPoints" ? accessPointsData : null);
    const cleanPayload = getCleanUpdatePayload({ ports });

    sendRequest(
      apiUrl,
      uuids.update,
      requestParams,
      { updates: cleanPayload }
    );
  }

  const onDiscard = () => {
    onBlockerDiscard();
    setSwitchesData(cloneDeep(originalSwitchesData));
    setAccessPointsData(cloneDeep(originalAccessPointsData));

    // Sync back to the last record to SW activeRow.
    const selectedSwitchActiveRow = originalSwitchesData.find(port => port.macAddress === activeRow.macAddress && port.portIndex === activeRow.portIndex);
    if (selectedSwitchActiveRow) {
      const clonedSwitchActiveRow = cloneDeep(selectedSwitchActiveRow);
      setActiveRow(clonedSwitchActiveRow);
      setOriginalRowData(clonedSwitchActiveRow);
    }

    // Sync back to the last record to AP activeRow.
    const selectedApActiveRow = originalAccessPointsData.find(port => port.macAddress === activeRow.macAddress && port.portIndex === activeRow.portIndex);
    if (selectedApActiveRow) {
      const clonedApActiveRow = cloneDeep(selectedApActiveRow);
      setActiveRow(clonedApActiveRow);
      setOriginalRowData(clonedApActiveRow);
    }
  }

  const flattenDevices = (devices) => {
    return devices.flatMap((device) => {
      const ports = device["wired.port"] || [];
      const switchPorts = device.switchPorts;
      const info = device.info || {};
      const capabilities = device.capabilities || {};

      return ports.map((port) => {
        const matchedSwitchPort = switchPorts.find(
          (sp) =>
            sp.portStaIndex === port.portIndex ||
            sp.portName === port.portName
        );

        const portIndex = port.portIndex;
        const switchPort = switchPorts.find(
          (sp) => sp.portStaIndex === portIndex
        );

        let allowVlan = "";
        let poePowerUsage = null;

        if (info.deviceType === "switch") {
          poePowerUsage = switchPort?.poePortPower;
          allowVlan =
            port.portType === defaultPortTypes.trunk.value
              ? port.allowVlan
              : "";
        } else {
          allowVlan = port.allowVlan ?? "";
        }

        return {
          ...port,
          ...info,
          ...capabilities,
          switchPort: matchedSwitchPort,
          allSwitchPorts: switchPorts,
          supportedSpeed: parseSupportedSpeed(
            port.linkSpeed,
            port.portIndex
          ),
          deviceName: info.deviceName || "-",
          portProfile: port.portProfile || "", // profile id
          portProfileName: "", // profile name
          speedValue: port.speed,
          speedS: port.speed,
          speedT: port.speed,
          speedLabel: getSpeedOptionsLabel(port.speed),
          portType: port.portType,
          state: matchedSwitchPort ? getSwitchPortStatus(matchedSwitchPort) : "-",
          stateLabel: matchedSwitchPort ? getSwitchPortStatusLabel(matchedSwitchPort) : "-",
          allowVlan,
          poePowerUsage,
          isPortScheduleMode: !!port.portSchedule,
          isPoeScheduleMode: !!port.poeSchedule,
        };
      });
    });
  }

  useEffect(() => {
    if (isAuthReady) {
      getPortTagsData();
    }
  }, [isAuthReady]);

  useEffect(() => {
    if (!dataMap?.[uuids.update]) return;

    const updateResponse = getData(uuids.update);

    if (!updateResponse || updateResponse?.code === 400 || updateResponse?.code === 500) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: updateResponse?.message,
      });
    } else {
      getAllWiredData();
      onBlockerApply();

      setAlertInfo({
        open: true,
        severity: "success",
        message: (
          <Box>
            <Box
              sx={{
                fontSize: theme.typography.customFontSize[3],
                fontWeight: theme.typography.customFontWeight[3],
                pb: 1
              }}
            >
              Applied
            </Box>
            <Box>Changes applied successfully.</Box>
          </Box>
        )
      })

    }

  }, [dataMap?.[uuids.update]]);

  useEffect(() => {
    if (!dataMap?.[uuids.readPortTags]) return;

    const res = getData(uuids.readPortTags);

    if (res?.data) {
      const map = Object.fromEntries(
        res.data.map(({ tagId, tagName }) => [tagId, tagName])
      );

      setPortTagsMap(map);
      getAllWiredData();

    }
  }, [dataMap?.[uuids.readPortTags]]);

  useEffect(() => {
    if (!dataMap?.[uuids.read]) return;

    const wiredResponse = getData(uuids.read);

    let switchPortsFlattened = [];
    let apPortsFlattened = [];

    if (wiredResponse?.data) {
      const allDevices = wiredResponse.data;

      const switches = allDevices.filter(item => item.info.deviceType === "switch");
      const aps = allDevices.filter(item => item.info.deviceType === "ap");

      switchPortsFlattened = flattenDevices(switches);
      apPortsFlattened = flattenDevices(aps);

      setAllSwitchesData(switchPortsFlattened);
      setAllAccessPointsData(apPortsFlattened);
      getProfileList();
      getScheduleProfiles();
      getMacProfiles();
      getRadiusProfiles();
    }
  }, [dataMap?.[uuids.read]]);

  useEffect(() => {
    if (!dataMap?.[uuids.readProfile]) return;

    const profileResponse = getData(uuids.readProfile);
    if (profileResponse?.data) {
      const allProfile = [defaultProfileList, ...profileResponse.data]
        .map(profile => {
          return {
            ...profile,
            label: profile.name,
            value: profile.id
          }
        });

      const apProfiles = allProfile.filter(item => item.deviceCategory === "ap" || item.isDefault);
      const switchProfiles = allProfile.filter(item => item.deviceCategory === "switch" || item.isDefault);

      setProfileAPList(apProfiles);
      setProfileSwitchList(switchProfiles);

      // Update portProfile names in switchesData
      const initSwitchData = allSwitchesData.map(port => {
        const selectedProfile = switchProfiles.find(profile => profile.id === port?.portProfile) ?? null;

        return {
          ...port,
          portProfileName: selectedProfile ? selectedProfile.name : "",
          selectedProfile,
          profileMap: selectedProfile,
        }
      })

      setAllSwitchesData(cloneDeep(initSwitchData));
      setSwitchesData(cloneDeep(initSwitchData));
      setOriginalSwitchesData(cloneDeep(initSwitchData));

      // Update portProfile names in accessPointsData
      const initAPData = allAccessPointsData.map(port => {
        const selectedProfile = apProfiles.find(profile => profile.id === port?.portProfile) ?? null;

        return {
          ...port,
          portProfileName: selectedProfile ? selectedProfile.name : "",
          selectedProfile,
          profileMap: selectedProfile,
        }
      })

      setAllAccessPointsData(cloneDeep(initAPData));
      setAccessPointsData(cloneDeep(initAPData));
      setOriginalAccessPointsData(cloneDeep(initAPData));

      displayGivenModel();
    }
  }, [dataMap?.[uuids.readProfile]])

  useEffect(() => {
    if (!dataMap?.[uuids.readSchedule]) return;

    const res = getData(uuids.readSchedule);
    if (res?.data) {
      setScheduleOptions((res.data || []).map((p) => ({ label: p.name, value: p.id })));
    }
  }, [dataMap?.[uuids.readSchedule]]);

  useEffect(() => {
    if (!dataMap?.[uuids.readRadiusProfile]) return;

    const res = getData(uuids.readRadiusProfile);
    if (res?.data) {
      setRadiusProfiles(res.data);
    }
  }, [dataMap?.[uuids.readRadiusProfile]]);

  useEffect(() => {
    if (!dataMap?.[uuids.readMacProfile]) return;

    const res = getData(uuids.readMacProfile);
    if (res?.data) {
      setMacProfiles(res.data);
    }
  }, [dataMap?.[uuids.readMacProfile]]);

  // useEffect(() => {
  //   if (navigateModelName && navigateMacAddress) {
  //     setSelectedModel((prev) => {
  //       const updated = {
  //         ...prev,
  //         ...(tabValue === "switches"
  //           ? {
  //             switches: navigateModelName,
  //             switchesSub: [navigateMacAddress],
  //           }
  //           : {
  //             accessPoints: navigateModelName,
  //             accessPointsSub: [navigateMacAddress],
  //           }),
  //       };

  //       if (tabValue === "switches") {
  //         let filtered = allSwitchesData.filter(
  //           (item) =>
  //             item.modelName === navigateModelName &&
  //             item.macAddress === navigateMacAddress
  //         );
  //         setSwitchesData(filtered);
  //       } else {
  //         let filtered = allAccessPointsData.filter(
  //           (item) =>
  //             item.modelName === navigateModelName &&
  //             item.macAddress === navigateMacAddress
  //         );
  //         setAccessPointsData(filtered);
  //       }

  //       return updated;
  //     });

  //     const {
  //       navigateModelName,
  //       navigateMacAddress,
  //       ...resetStates
  //     } = location?.state;

  //     navigate(location.pathname, {
  //       replace: true,
  //       state: resetStates
  //     });

  //   } else {
  //     setSelectedModel({
  //       switches: "All Models",
  //       switchesSub: ["All devices"],
  //       accessPoints: "All Models",
  //       accessPointsSub: ["All devices"],
  //     });

  //     if (tabValue === "switches") {
  //       setSwitchesData(allSwitchesData);
  //     } else {
  //       setAccessPointsData(allAccessPointsData);
  //     }
  //   }
  // }, [
  //   navigateModelName,
  //   navigateMacAddress,
  //   tabValue,
  //   allSwitchesData,
  //   allAccessPointsData,
  // ]);

  useEffect(() => {
    if (tabValue === "switches") {
      let filtered = allSwitchesData;
      if (selectedModel.switches && selectedModel.switches !== "All Models") {
        filtered = filtered.filter(
          (item) => item.modelName === selectedModel.switches
        );
      }

      if (
        selectedModel.switchesSub &&
        selectedModel.switchesSub.length > 0 &&
        !selectedModel.switchesSub.includes("All devices")
      ) {
        filtered = filtered.filter((item) =>
          selectedModel.switchesSub.includes(item.macAddress)
        );
      }

      setSwitchesData(cloneDeep(filtered));
      setOriginalSwitchesData(cloneDeep(filtered));
      onInitBlockerData(cloneDeep(filtered));
    } else if (tabValue === "accessPoints") {
      let filtered = allAccessPointsData;

      if (
        selectedModel.accessPoints &&
        selectedModel.accessPoints !== "All Models"
      ) {
        filtered = filtered.filter(
          (item) => item.modelName === selectedModel.accessPoints
        );
      }

      if (
        selectedModel.accessPointsSub &&
        selectedModel.accessPointsSub.length > 0 &&
        !selectedModel.accessPointsSub.includes("All devices")
      ) {
        filtered = filtered.filter((item) =>
          selectedModel.accessPointsSub.includes(item.macAddress)
        );
      }

      setAccessPointsData(cloneDeep(filtered));
      setOriginalAccessPointsData(cloneDeep(filtered));
      onInitBlockerData(cloneDeep(filtered));
    }

    if (navigatePortProfileName) {
      currentTableInstance.setGlobalFilter(navigatePortProfileName);

      const {
        navigatePortProfileName: tmpNavigatePortProfileName,
        ...resetStates
      } = location?.state;

      navigate(location.pathname, {
        replace: true,
        state: resetStates
      });
    }

  }, [selectedModel, tabValue, allSwitchesData, allAccessPointsData]);

  useEffect(() => {
    if (navigatePortDeviceType) {

      const {
        navigatePortDeviceType: _removeDeviceType,
        ...resetStates
      } = location?.state;

      navigate(location.pathname, {
        replace: true,
        state: resetStates
      });
    }

  }, [navigatePortDeviceType])

  useEffect(() => {
    if (navigatePortDeviceType && navigateSelectedPort) {
      let matchedPort = null;

      if (navigatePortDeviceType === "switches") {
        matchedPort = allSwitchesData.find(port => navigateSelectedPort === port.portIndex);
      } else if (navigatePortDeviceType === "accessPoints") {
        matchedPort = allAccessPointsData.find(port => navigateSelectedPort === port.portIndex);
      }

      if (matchedPort) {
        setTabValue(navigatePortDeviceType);
        setOriginalRowData(cloneDeep(matchedPort));
        setActiveRow(cloneDeep(matchedPort));
        setDrawerOpen(true);

        const {
          navigatePortDeviceType: _removeDeviceType,
          navigateSelectedPort: _removeSelectedPort,
          ...resetStates
        } = location?.state;

        navigate(location.pathname, {
          replace: true,
          state: resetStates
        });
      }
    }
  }, [navigatePortDeviceType, navigateSelectedPort, allSwitchesData, allAccessPointsData])

  useEffect(() => {
    if (isDataChanged) {
      setGlobalOnApplyFn(onApply);
      setGlobalOnDiscardFn(onDiscard);
    } else {
      setGlobalOnApplyFn(null);
      setGlobalOnDiscardFn(null);
    }
  }, [isDataChanged])

  return (
    <>
      <Box sx={{ padding: "20px" }}>
        {
          isDataChanged &&
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: "30px",
            }}
          >
            <SolidRectangleButton
              width="150px"
              height="32px"
              onClick={() => onApply()}
            >
              Apply Changes
            </SolidRectangleButton>
            <OutlineRectangleButton
              width="150px"
              height="32px"
              onClick={() => onDiscard()}
            >
              Discard Changes
            </OutlineRectangleButton>
          </Box>
        }
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[1],
              marginBottom: "10px",
            }}
          >
            To batch edit switch ports, please select a model first
          </Typography>

          <Box sx={{ display: "flex", gap: "16px" }}>
            <Box
              ref={modelSelectWrapperRef}
              onClickCapture={(e) => {
                if (isDataChanged && !isAutoClickRef.current) {
                  e.stopPropagation();
                  tabBlocker(() => {
                    isAutoClickRef.current = true;
                    const target = modelSelectWrapperRef.current?.firstElementChild;
                    if (target) {
                      target.click();
                    }
                    isAutoClickRef.current = false;
                  })();
                }
              }}
            >
              <StyledSelect
                placeholder="Select Model"
                value={
                  tabValue === "switches"
                    ? selectedModel.switches || "All Models"
                    : selectedModel.accessPoints || "All Models"
                }
                onChange={(opt) => {
                  setSelectedModel((prev) => ({
                    ...prev,
                    ...(tabValue === "switches"
                      ? { switches: opt.value, switchesSub: [] }
                      : { accessPoints: opt.value, accessPointsSub: [] }),
                  }))

                  if (opt.value === "All Models") {
                    currentTableInstance.table.resetRowSelection();
                  }
                }}
                options={[
                  { value: "All Models", label: "All Models" },
                  ...(tabValue === "switches"
                    ? allSwitchesData
                      .filter((s) => !s.tunnelAccess)
                      .map((s) => ({
                        value: s.modelName,
                        label: s.modelName,
                      }))
                    : allAccessPointsData.map((ap) => ({
                      value: ap.modelName,
                      label: ap.modelName,
                    }))),
                ].filter(
                  (item, index, self) =>
                    index === self.findIndex((t) => t.value === item.value)
                )}
                width="266px"
              />
            </Box>

            {(tabValue === "switches"
              ? selectedModel.switches
              : selectedModel.accessPoints) &&
              (tabValue === "switches"
                ? selectedModel.switches !== "All Models"
                : selectedModel.accessPoints !== "All Models") && (
                <Box
                  ref={deviceSelectWrapperRef}
                  onClickCapture={(e) => {
                    if (isDataChanged && !isAutoClickRef.current) {
                      e.stopPropagation();
                      tabBlocker(() => {
                        isAutoClickRef.current = true;
                        const target = deviceSelectWrapperRef.current?.firstElementChild;
                        if (target) {
                          target.click();
                        }
                        isAutoClickRef.current = false;
                      })();
                    }
                  }}
                >
                  <SearchWithSelectCheckbox
                    placeholder="Select Device"
                    value={
                      tabValue === "switches"
                        ? selectedModel.switchesSub || []
                        : selectedModel.accessPointsSub || []
                    }
                    onChange={(newValues) =>
                      setSelectedModel((prev) => ({
                        ...prev,
                        ...(tabValue === "switches"
                          ? { switchesSub: newValues }
                          : { accessPointsSub: newValues }),
                      }))
                    }
                    options={[
                      {
                        value: "All devices",
                        label: "All devices",
                        withCheckbox: false,
                      },
                      ...(tabValue === "switches"
                        ? allSwitchesData
                          .filter((s) => s.modelName === selectedModel.switches)
                          .reduce((acc, curr) => {
                            if (
                              !acc.find((d) => d.macAddress === curr.macAddress)
                            ) {
                              acc.push(curr);
                            }
                            return acc;
                          }, [])
                          .map((s) => ({
                            value: s.macAddress,
                            label: (
                              <Box onClick={(e) => e.stopPropagation()}>
                                <Box
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: theme.typography.customFontSize[2],
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    overflowX: "hidden",
                                  }}
                                >
                                  {s.deviceName}
                                </Box>
                                <Box
                                  sx={{
                                    color: theme.palette.primary.greyThird,
                                    fontSize: theme.typography.customFontSize[0],
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    overflowX: "hidden",
                                  }}
                                >
                                  {s.macAddress}
                                </Box>
                              </Box>
                            ),
                            withCheckbox: true,
                          }))
                        : allAccessPointsData
                          .filter(
                            (ap) => ap.modelName === selectedModel.accessPoints
                          )
                          .reduce((acc, curr) => {
                            if (
                              !acc.find((d) => d.macAddress === curr.macAddress)
                            ) {
                              acc.push(curr);
                            }
                            return acc;
                          }, [])
                          .map((ap) => ({
                            value: ap.macAddress,
                            label: (
                              <Box onClick={(e) => e.stopPropagation()}>
                                <Box
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: theme.typography.customFontSize[2],
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    overflowX: "hidden",
                                  }}
                                >
                                  {ap.deviceName}
                                </Box>
                                <Box
                                  sx={{
                                    color: theme.palette.primary.greyThird,
                                    fontSize: theme.typography.customFontSize[0],
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    overflowX: "hidden",
                                  }}
                                >
                                  {ap.macAddress}
                                </Box>
                              </Box>
                            ),
                            withCheckbox: true,
                          }))),
                    ]}
                    width="266px"
                    withCheckbox
                  />
                </Box>
              )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "15px",
          }}
        >
          {selectedRows?.length > 0 &&
            showEditGeneral &&
            (
              <>
                <SolidRectangleLightButton
                  width="100px"
                  height="32px"
                  iconStart={<EditIcon />}
                  onClick={
                    tabValue === "switches"
                      ? handleOpenSwitchEditModal
                      : handleOpenAPEditModal
                  }
                >
                  Edit
                </SolidRectangleLightButton>
                <Divider
                  sx={{
                    borderColor: theme.palette.primary.greyThread,
                    width: "1px",
                    margin: "20px 20px",
                    minHeight: "50px",
                  }}
                  orientation="vertical"
                  flexItem
                />
              </>
            )}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              flex: 1,
              minHeight: "auto",
              "& .MuiTabs-indicator": {
                backgroundColor: theme.palette.primary.main,
                height: "5px",
                borderRadius: theme.shape.customBorderRadius[0],
              },
              ".MuiTabs-flexContainer": {
                gap: "20px",
              },
            }}
          >
            {["switches", "accessPoints"].map((tab) => {
              const count =
                tab === "switches"
                  ? switchesData.length
                  : accessPointsData.length;
              return (
                <Tab
                  key={tab}
                  value={tab}
                  sx={{
                    minHeight: "auto",
                    textTransform: "none",
                    fontWeight:
                      tabValue === tab
                        ? theme.typography.customFontWeight[1]
                        : theme.typography.customFontWeight[0],
                    color:
                      tabValue === tab
                        ? theme.palette.primary.main
                        : theme.palette.primary.greySecound,
                    fontSize: theme.typography.customFontSize[3],
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <span>{formatTabLabel(tab)}</span>
                      <span
                        style={{
                          backgroundColor:
                            tabValue === tab
                              ? theme.palette.primary.main
                              : theme.palette.primary.greyThird,
                          color: theme.palette.primary.white,
                          borderRadius: theme.shape.customBorderRadius[4],
                          width: "38px",
                          height: "22px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: theme.typography.customFontSize[3],
                        }}
                      >
                        {count}
                      </span>
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: theme.palette.primary.greyThread,
                minHeight: "50px",
                width: "1px",
                margin: "20px 20px",
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SearchInput
                placeholder="Search by Name, LLDP"
                value={currentTableInstance.globalFilter}
                onChange={currentTableInstance.setGlobalFilter}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  ml: 1,
                }}
              >
                <ColumnFilterToggle
                  {...currentTableInstance}
                  filterData={
                    tabValue === "switches"
                      ? [
                        "state",
                        "portProfileName",
                        "portTypeLabel",
                        "operationLabel",
                        "allowVlan",
                        "poeState",
                        "modelName",
                        "rstp",
                        "lbdState",
                        "ddpState",
                        "stpGuard",
                        "tagsLabel"
                      ]
                      : [
                        "state",
                        "portProfileName",
                        "allowVlan",
                        "tagsLabel",
                        "modelName"
                      ]
                  }
                />
                <Box
                  onMouseEnter={() => setOptionsHover("options")}
                  onMouseLeave={() => setOptionsHover(null)}
                  sx={{ cursor: "pointer" }}
                >
                  <ColumnVisibilityToggle
                    maxWidth={tabValue === "switches" ? 800 : 600}
                    {...currentTableInstance}
                    groupedData={
                      tabValue === "switches"
                        ? switchesOptionsData
                        : apOptionsData
                    }
                    hiddenCheck={["deviceName", "portIndex", "state"]}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ width: "100%" }}>
          <TableField
            {...currentTableInstance}
            drawerOpen={drawerOpen}
            handleRowOnClick={(row) => {
              setOriginalRowData(row);
              setActiveRow(row);
              setDrawerOpen(true);
            }}
          />
          <DrawerField open={drawerOpen} handleClose={handleDrawerClose} contentKey={`PortManagement${uuidv4()}`} width={620}>
            {tabValue === "switches" ? (
              <SwitchesDrawer
                key={tabValue}
                auth={auth}
                setAlertInfo={setAlertInfo}
                profileList={profileSwitchList}
                drawerOpen={drawerOpen}
                linkSpeedOptions={linkSpeedOptions}
                portTypeOptions={portTypeOptions}
                activeRow={activeRow}
                setActiveRow={setActiveRow}
                originalRowData={originalRowData}
                setOriginalRowData={setOriginalRowData}
                switchesData={switchesData}
                setSwitchesData={setSwitchesData}
                onUpdateBlockerData={onUpdateBlockerData}
                profileSwitchList={profileSwitchList}
                initCallback={getAllWiredData}
                getSupportedSpeedOptions={getSupportedSpeedOptions}
                showEditGeneral={showEditGeneral}
                scheduleOptions={scheduleOptions}
                radiusProfiles={radiusProfiles}
                macProfiles={macProfiles}
                onGoToPage={onGoToPage}
              />
            ) : (
              <AccessPointsDrawer
                key={tabValue}
                auth={auth}
                setAlertInfo={setAlertInfo}
                profileList={profileAPList}
                linkSpeedOptions={linkSpeedOptions}
                portTypeOptions={portTypeOptions}
                drawerOpen={drawerOpen}
                activeRow={activeRow}
                setActiveRow={setActiveRow}
                originalRowData={originalRowData}
                setOriginalRowData={setOriginalRowData}
                accessPointsData={accessPointsData}
                setAccessPointsData={setAccessPointsData}
                onUpdateBlockerData={onUpdateBlockerData}
                profileAPList={profileAPList}
                initCallback={getAllWiredData}
                showEditGeneral={showEditGeneral}
              />
            )}
          </DrawerField>
        </Box>
      </Box>

      <SwitchEditModal
        setAlertInfo={setAlertInfo}
        openSwitchEditModal={openSwitchEditModal}
        setOpenSwitchEditModal={setOpenSwitchEditModal}
        handleCloseEditModal={handleCloseSwitchEditModal}
        selectedRows={selectedRows}
        switchesData={switchesData}
        setSwitchesData={setSwitchesData}
        originalRowData={originalRowData}
        setOriginalRowData={setOriginalRowData}
        onUpdateBlockerData={onUpdateBlockerData}
        profileSwitchList={profileSwitchList}
        linkSpeedOptions={linkSpeedOptions}
        initCallback={getAllWiredData}
        onEditCallback={(editedValues, newSwitchesData) => {
          // Sync back to the current updated record to SW activeRow.
          if (activeRow && newSwitchesData) {
            const updatedActiveRow = newSwitchesData.find(p => p.id === activeRow.id);
            if (updatedActiveRow) {
              setActiveRow(cloneDeep(updatedActiveRow));
              setOriginalRowData(cloneDeep(updatedActiveRow));
              return;
            }
          }
          if (editedValues) {
            const clonedApActiveRow = cloneDeep(editedValues);
            setActiveRow(clonedApActiveRow);
            setOriginalRowData(clonedApActiveRow);
          }
        }}
        showEditGeneral={showEditGeneral}
        getSupportedSpeedOptions={getSupportedSpeedOptions}
        scheduleOptions={scheduleOptions}
        radiusProfiles={radiusProfiles}
        macProfiles={macProfiles}
        onGoToPage={onGoToPage}
      />

      <AccessPointEditModal
        setAlertInfo={setAlertInfo}
        openAPEditModal={openAPEditModal}
        setOpenAPEditModal={setOpenAPEditModal}
        handleCloseAPEditModal={handleCloseAPEditModal}
        selectedRows={selectedRows}
        accessPointsData={accessPointsData}
        setAccessPointsData={setAccessPointsData}
        originalRowData={originalRowData}
        setOriginalRowData={setOriginalRowData}
        onUpdateBlockerData={onUpdateBlockerData}
        profileAPList={profileAPList}
        initCallback={getAllWiredData}
        onEditCallback={editedValues => {
          // Sync back to the current updated record to AP activeRow.
          if (editedValues) {
            const clonedApActiveRow = cloneDeep(editedValues);
            setActiveRow(clonedApActiveRow);
            setOriginalRowData(clonedApActiveRow);
          }
        }}
        showEditGeneral={showEditGeneral}
      />

      <UnSavedConfirmModalContainer
        isOpen={isUnsavedBlocked}
        applyCallback={() => onApply()}
        discardCallback={() => onDiscard()}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};
export default WiredPortManagement;
